// D:\myProject_sw\worldlandcafe\client\src\components\Technology.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap,
  FileText,
  ExternalLink,
  Search,
  Calendar,
  Tag,
  Upload,
  Plus,
  X,
  Check,
  AlertCircle,
  Trash2,
  Edit3,
  Eye,
  User,
  Image,
  FileType,
  Sheet,
  Presentation,
  Download,
  ChevronLeft
} from 'lucide-react';
import { auth } from '../firebase';
import './Technology.css';
import './Utils/common_Utils.css';

const Technology = ({ user, userProfile }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: 'technical',
    tags: [],
    tagInput: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState(null);

  const categories = [
    { id: 'all', name: '전체', icon: FileText },
    { id: 'technical', name: '기술문서', icon: Zap },
    { id: 'report', name: '리포트', icon: FileText },
    { id: 'tutorial', name: '튜토리얼', icon: FileText },
    { id: 'image', name: '이미지', icon: Image }
  ];

  // 파일 타입별 아이콘 매핑
  const getFileIcon = (fileName, fileType) => {
    const extension = fileName.toLowerCase().split('.').pop();

    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'webp':
        return Image;
      case 'pdf':
        return FileText;
      case 'docx':
      case 'doc':
        return FileText;
      case 'xlsx':
      case 'xls':
        return Sheet;
      case 'pptx':
      case 'ppt':
        return Presentation;
      case 'hwpx':
      case 'hwp':
        return FileText;
      case 'html':
      case 'htm':
        return Zap;
      default:
        return FileType;
    }
  };

  // 파일 타입 확인
  const getFileType = (fileName) => {
    const extension = fileName.toLowerCase().split('.').pop();

    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
      return 'image';
    } else if (extension === 'pdf') {
      return 'pdf';
    } else if (['docx', 'doc'].includes(extension)) {
      return 'document';
    } else if (['xlsx', 'xls'].includes(extension)) {
      return 'spreadsheet';
    } else if (['pptx', 'ppt'].includes(extension)) {
      return 'presentation';
    } else if (['hwpx', 'hwp'].includes(extension)) {
      return 'hwp';
    } else if (['html', 'htm'].includes(extension)) {
      return 'html';
    }
    return 'unknown';
  };

  // 환경에 따른 서버 URL 설정
  const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3002';

  // 알림 표시 함수
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // 문서 목록 로드
  const loadDocuments = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);

      const response = await fetch(`${SERVER_URL}/api/documents?${params}`);
      const data = await response.json();

      if (data.success) {
        setDocuments(data.documents);
      } else {
        showNotification('문서 목록을 불러오는 중 오류가 발생했습니다.', 'error');
      }
    } catch (error) {
      console.error('문서 로드 오류:', error);
      showNotification('문서 목록을 불러오는 중 오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [selectedCategory, searchTerm]);

  // 파일 유효성 검사
  const validateFile = (file) => {
    if (!file) return '파일을 선택해주세요.';

    const allowedExtensions = ['html', 'htm', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'hwpx', 'hwp'];
    const fileName = file.name.toLowerCase();
    const extension = fileName.split('.').pop();

    if (!allowedExtensions.includes(extension)) {
      return '지원하지 않는 파일 형식입니다. (지원 형식: HTML, 이미지, PDF, DOCX, XLSX, PPTX, HWPX)';
    }

    if (file.size > 50 * 1024 * 1024) { // 50MB
      return '파일 크기는 50MB 이하여야 합니다.';
    }

    return null;
  };

  // 태그 추가
  const addTag = () => {
    const tag = uploadData.tagInput.trim();
    if (tag && !uploadData.tags.includes(tag) && uploadData.tags.length < 5) {
      setUploadData(prev => ({
        ...prev,
        tags: [...prev.tags, tag],
        tagInput: ''
      }));
    }
  };

  // 태그 제거
  const removeTag = (tagToRemove) => {
    setUploadData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // 파일 업로드
  const handleFileUpload = async (e) => {
    e.preventDefault();

    if (!user) {
      showNotification('로그인이 필요합니다.', 'error');
      return;
    }

    if (!userProfile?.nickname) {
      showNotification('닉네임 설정이 필요합니다.', 'error');
      return;
    }

    // 유효성 검사
    if (!uploadData.title.trim()) {
      showNotification('제목을 입력해주세요.', 'error');
      return;
    }

    if (!uploadData.description.trim()) {
      showNotification('설명을 입력해주세요.', 'error');
      return;
    }

    const fileError = validateFile(uploadFile);
    if (fileError) {
      showNotification(fileError, 'error');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description);
      formData.append('category', uploadData.category);
      formData.append('tags', uploadData.tags.join(','));
      formData.append('userInfo', JSON.stringify({
        uid: user.uid,
        email: user.email,
        nickname: userProfile.nickname,
        displayName: user.displayName,
        photoURL: user.photoURL
      }));

      const response = await fetch(`${SERVER_URL}/api/documents/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        showNotification('파일이 성공적으로 업로드되었습니다!');
        setIsUploadModalOpen(false);
        setUploadData({
          title: '',
          description: '',
          category: 'technical',
          tags: [],
          tagInput: ''
        });
        setUploadFile(null);

        // 보안 경고가 있다면 표시
        if (data.securityWarnings && data.securityWarnings.length > 0) {
          showNotification('보안 검사에서 일부 이슈가 발견되었습니다. 업로드는 완료되었지만 내용을 확인해주세요.', 'warning');
        }

        // 문서 목록 새로고침
        loadDocuments();
      } else {
        showNotification(data.error || '업로드 중 오류가 발생했습니다.', 'error');
      }

    } catch (error) {
      console.error('업로드 오류:', error);
      showNotification('업로드 중 오류가 발생했습니다.', 'error');
    } finally {
      setUploading(false);
    }
  };

  // 문서 삭제
  const deleteDocument = async (documentId) => {
    if (!user) {
      showNotification('로그인이 필요합니다.', 'error');
      return;
    }

    if (!window.confirm('정말로 이 파일을 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(`${SERVER_URL}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userInfo: JSON.stringify({
            uid: user.uid,
            email: user.email,
            nickname: userProfile?.nickname || user.displayName || user.email,
            displayName: user.displayName || userProfile?.nickname || user.email,
            photoURL: user.photoURL || null
          })
        })
      });

      const data = await response.json();

      if (data.success) {
        showNotification('파일이 삭제되었습니다.');
        loadDocuments();
      } else {
        showNotification(data.error || '삭제 중 오류가 발생했습니다.', 'error');
      }

    } catch (error) {
      console.error('삭제 오류:', error);
      showNotification('삭제 중 오류가 발생했습니다.', 'error');
    }
  };

  // 문서 열기
  const openDocument = (document) => {
    const fileType = getFileType(document.file_name);
    const encodedFileName = encodeURIComponent(document.file_name);
    const url = `${SERVER_URL}/files/${encodedFileName}`;

    if (fileType === 'html') {
      // HTML 파일은 직접 파일 URL로 열기
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      if (isMobile) {
        window.location.href = url;
      } else {
        window.open(url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      }
    } else if (fileType === 'image') {
      // 이미지는 새 창에서 바로 열기
      window.open(url, '_blank');
    } else {
      // 다른 파일들은 뷰어 페이지로 이동
      const viewerUrl = `${SERVER_URL}/viewer/${document.id}`;
      window.open(viewerUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
    }
  };

  // 파일 다운로드
  const downloadFile = (document) => {
    const encodedFileName = encodeURIComponent(document.file_name);
    const url = `${SERVER_URL}/files/${encodedFileName}`;
    const link = document.createElement('a');
    link.href = url;

    // 원본 파일명을 UTF-8로 제대로 설정
    const originalFileName = document.original_file_name || document.file_name;
    link.download = originalFileName;

    // 브라우저별 호환성을 위한 추가 속성
    link.setAttribute('download', originalFileName);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 문서 필터링
  const filteredDocuments = documents.filter(doc => {
    const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
    const matchesSearch = searchTerm === '' ||
      doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));

    return matchesCategory && matchesSearch;
  });

  const canDeleteDocument = (document) => {
    if (!user || !userProfile) return false;
    return document.uploader_email === user.email || user.email === 'gategil1149@gmail.com';
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="technology">
      {/* 알림 */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-content">
            {notification.type === 'error' && <AlertCircle className="notification-icon" />}
            {notification.type === 'success' && <Check className="notification-icon" />}
            {notification.type === 'warning' && <AlertCircle className="notification-icon" />}
            <span>{notification.message}</span>
          </div>
        </div>
      )}



      <div className="technology-header">
        {/* <div 
          onClick={handleBack}  
          className="common-back-button"
        >
          <ChevronLeft size={24} />
        </div> */}
        {/* <h2 className="technology-title">Document Repository</h2> */}

      </div>

      {/* 업로드 버튼 */}
      {user?.email === 'gategil1149@gmail.com' && userProfile && (
        <div className="upload-section">
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="upload-btn"
          >
            <Upload className="upload-icon" />
            <span>파일 업로드</span>
          </button>
        </div>
      )}

      {/* 검색 및 필터 */}
      <div className="technology-controls">
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="파일 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="category-filters">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
            >
              <category.icon className="category-icon" />
              <span>{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 문서 목록 */}
      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>파일 목록을 불러오는 중...</p>
        </div>
      ) : (
        <div className="documents-grid">
          {filteredDocuments.length > 0 ? (
            filteredDocuments.map((doc) => {
              const FileIcon = getFileIcon(doc.file_name, doc.file_type);
              const fileType = getFileType(doc.file_name);

              return (
                <div key={doc.id} className="document-card">
                  <div className="document-header">
                    <div className="document-icon">
                      <FileIcon className="icon" />
                    </div>
                    <div className="document-date">
                      <Calendar className="date-icon" />
                      <span>{new Date(doc.upload_date).toLocaleDateString('ko-KR')}</span>
                    </div>
                  </div>

                  <div className="document-content">
                    <h3 className="document-title">{doc.title}</h3>
                    <p className="document-description">{doc.description}</p>

                    <div className="document-meta">
                      <div className="document-uploader">
                        <User className="uploader-icon" />
                        <span>{doc.uploader_nickname}</span>
                      </div>
                      <div className="document-views">
                        <Eye className="views-icon" />
                        <span>{doc.view_count}</span>
                      </div>
                    </div>

                    {doc.tags && doc.tags.length > 0 && (
                      <div className="document-tags">
                        {doc.tags.map((tag, index) => (
                          <span key={index} className="document-tag">
                            <Tag className="tag-icon" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="file-info">
                      <FileIcon className="file-icon" />
                      <span>{doc.original_file_name}</span>
                      <span className="file-size">({(doc.file_size / 1024).toFixed(1)}KB)</span>
                    </div>
                  </div>

                  <div className="document-actions">
                    <button
                      onClick={() => openDocument(doc)}
                      className="document-btn primary"
                    >
                      <ExternalLink className="btn-icon" />
                      <span>{fileType === 'image' ? '이미지 보기' : '파일 보기'}</span>
                    </button>

                    <button
                      onClick={() => downloadFile(doc)}
                      className="document-btn secondary"
                    >
                      <Download className="btn-icon" />
                    </button>

                    {canDeleteDocument(doc) && (
                      <button
                        onClick={() => deleteDocument(doc.id)}
                        className="document-btn danger"
                      >
                        <Trash2 className="btn-icon" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-documents">
              <FileText className="no-docs-icon" />
              <h3>검색 결과가 없습니다</h3>
              <p>다른 검색어나 카테고리를 시도해보세요.</p>
            </div>
          )}
        </div>
      )}

      {/* 업로드 모달 */}
      {isUploadModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>파일 업로드</h3>
              <button
                onClick={() => setIsUploadModalOpen(false)}
                className="modal-close"
              >
                <X className="close-icon" />
              </button>
            </div>

            <form onSubmit={handleFileUpload} className="upload-form">
              <div className="form-group">
                <label htmlFor="title">제목 *</label>
                <input
                  type="text"
                  id="title"
                  value={uploadData.title}
                  onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="파일 제목을 입력하세요"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">설명 *</label>
                <textarea
                  id="description"
                  value={uploadData.description}
                  onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="파일에 대한 설명을 입력하세요"
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">카테고리</label>
                <select
                  id="category"
                  value={uploadData.category}
                  onChange={(e) => setUploadData(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="technical">기술문서</option>
                  <option value="magazine">매거진</option>
                  <option value="whitepaper">백서</option>
                  <option value="report">리포트</option>
                  <option value="tutorial">튜토리얼</option>
                  <option value="news">뉴스</option>
                  <option value="image">이미지</option>
                  <option value="presentation">프레젠테이션</option>
                  <option value="spreadsheet">스프레드시트</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="tags">태그 (최대 5개)</label>
                <div className="tag-input-container">
                  <input
                    type="text"
                    id="tags"
                    value={uploadData.tagInput}
                    onChange={(e) => setUploadData(prev => ({ ...prev, tagInput: e.target.value }))}
                    placeholder="태그를 입력하고 엔터를 누르세요"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="tag-add-btn"
                    disabled={uploadData.tags.length >= 5}
                  >
                    <Plus className="plus-icon" />
                  </button>
                </div>

                {uploadData.tags.length > 0 && (
                  <div className="tag-list">
                    {uploadData.tags.map((tag, index) => (
                      <span key={index} className="tag-item">
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="tag-remove"
                        >
                          <X className="remove-icon" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="file">파일 선택 *</label>
                <input
                  type="file"
                  id="file"
                  accept=".html,.htm,.jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.hwpx,.hwp"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  required
                />
                {uploadFile && (
                  <div className="file-info">
                    <FileText className="file-icon" />
                    <span>{uploadFile.name}</span>
                    <span className="file-size">({(uploadFile.size / 1024).toFixed(1)}KB)</span>
                  </div>
                )}
                <div className="file-types-info">
                  <small>지원 형식: HTML, 이미지(JPG, PNG, GIF 등), PDF, DOCX, XLSX, PPTX, HWPX (최대 50MB)</small>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => setIsUploadModalOpen(false)}
                  className="btn-secondary"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="btn-primary"
                >
                  {uploading ? '업로드 중...' : '업로드'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Technology;